// =====================================================
// TARIFFS ADMIN PAGE - Tarifas
// REDISEÑADO: Modal de actos con vista dual, búsqueda,
// edición inline, batch y UX mejorada
// CORREGIDO: Stats bar con info útil, precio >= 0
// =====================================================

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import {
  Receipt,
  Plus,
  Search,
  RefreshCw,
  Edit2,
  Power,
  Loader2,
  Save,
  X,
  AlertCircle,
  FileText,
  Calendar,
  Euro,
  Stethoscope,
  Trash2,
  Filter,
  FileSpreadsheet,
  ChevronRight,
  Check,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  Hash,
  Upload,
  Download,
  Package,
} from 'lucide-react';
import { policiesApi } from '../../api/policies';
import { tariffsApi } from '../../api/tariffs';
import { medicalActsApi } from '../../api/medical-acts';
import { tariffsImportExport, tariffsBulkImport } from '../../api/import-export';
import type { BulkImportPreview, BulkImportResult } from '../../api/import-export';
import type { PolicySelect } from '../../api/policies';
import type { Tariff, TariffAct } from '../../api/tariffs';
import type { MedicalActSelect } from '../../api/medical-acts';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { ImportExportModal } from '../../components/ui/ImportExportModal';
import { toast } from '../../components/ui/Toast';

interface TariffFormData {
  policyId: string;
  code: string;
  name: string;
  description: string;
  validFrom: string;
  validTo: string;
}

export function TariffsAdminPage() {
  // Data state
  const [items, setItems] = useState<Tariff[]>([]);
  const [filteredItems, setFilteredItems] = useState<Tariff[]>([]);
  const [policies, setPolicies] = useState<PolicySelect[]>([]);
  const [medicalActs, setMedicalActs] = useState<MedicalActSelect[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPolicyId, setFilterPolicyId] = useState<string>('');

  // Sorting and pagination
  type SortField = 'name' | 'code' | 'createdAt';
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Tariff | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Acts modal state - REDISEÑADO
  const [isActsModalOpen, setIsActsModalOpen] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState<Tariff | null>(null);
  const [tariffActs, setTariffActs] = useState<TariffAct[]>([]);
  const [isLoadingActs, setIsLoadingActs] = useState(false);
  const [isSavingActs, setIsSavingActs] = useState(false);

  // Panel izquierdo: actos disponibles
  const [availableSearch, setAvailableSearch] = useState('');
  const [selectedAvailableIds, setSelectedAvailableIds] = useState<Set<string>>(new Set());
  const [batchPrice, setBatchPrice] = useState('');

  // Panel derecho: actos en tarifa
  const [tariffSearch, setTariffSearch] = useState('');
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [editingPriceValue, setEditingPriceValue] = useState('');

  // Import/Export modal
  const [isImportExportOpen, setIsImportExportOpen] = useState(false);

  // Bulk Import modal
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState<File | null>(null);
  const [bulkImportPreview, setBulkImportPreview] = useState<BulkImportPreview | null>(null);
  const [isBulkImportLoading, setIsBulkImportLoading] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState<BulkImportResult | null>(null);
  const bulkImportInputRef = useRef<HTMLInputElement>(null);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    item: Tariff | null;
    action: 'activate' | 'deactivate';
  }>({ isOpen: false, item: null, action: 'deactivate' });
  const [isProcessing, setIsProcessing] = useState(false);

  // Form
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TariffFormData>({
    defaultValues: {
      policyId: '',
      code: '',
      name: '',
      description: '',
      validFrom: '',
      validTo: '',
    },
  });

  // Load policies
  const loadPolicies = useCallback(async () => {
    try {
      const result = await policiesApi.getForSelect();
      setPolicies(result);
    } catch (err: any) {
      console.error('Error loading policies:', err);
    }
  }, []);

  // Load medical acts
  const loadMedicalActs = useCallback(async () => {
    try {
      const result = await medicalActsApi.getForSelect();
      setMedicalActs(result);
    } catch (err: any) {
      console.error('Error loading medical acts:', err);
    }
  }, []);

  // Load tariffs
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await tariffsApi.getAll(showInactive, filterPolicyId || undefined);
      setItems(result);
    } catch (err: any) {
      setError(err.message || 'Error al cargar tarifas');
    } finally {
      setIsLoading(false);
    }
  }, [showInactive, filterPolicyId]);

  useEffect(() => {
    loadPolicies();
    loadMedicalActs();
  }, [loadPolicies, loadMedicalActs]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter, sort and paginate items
  useEffect(() => {
    let result = items;

    // Filtrar por búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (item) =>
          item.code.toLowerCase().includes(query) ||
          item.name.toLowerCase().includes(query) ||
          item.policyName?.toLowerCase().includes(query) ||
          item.insuranceCompanyName?.toLowerCase().includes(query)
      );
    }

    // Ordenar
    result = [...result].sort((a, b) => {
      let aVal: any, bVal: any;
      switch (sortBy) {
        case 'code':
          aVal = a.code.toLowerCase();
          bVal = b.code.toLowerCase();
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt || 0).getTime();
          bVal = new Date(b.createdAt || 0).getTime();
          break;
        default:
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredItems(result);
  }, [items, searchQuery, sortBy, sortOrder]);

  // Paginación calculada
  const totalItems = filteredItems.length;
  const totalPages = Math.ceil(totalItems / limit);
  const paginatedItems = filteredItems.slice((page - 1) * limit, page * limit);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, showInactive, filterPolicyId, sortBy, sortOrder, limit]);

  // Computed: actos disponibles (no en tarifa) filtrados
  const filteredAvailableActs = useMemo(() => {
    const inTariffIds = new Set(tariffActs.map((ta) => ta.medicalActId));
    let available = medicalActs.filter((act) => !inTariffIds.has(act.id));

    if (availableSearch.trim()) {
      const query = availableSearch.toLowerCase();
      available = available.filter(
        (act) =>
          act.code.toLowerCase().includes(query) ||
          act.name.toLowerCase().includes(query)
      );
    }

    return available;
  }, [medicalActs, tariffActs, availableSearch]);

  // Computed: actos en tarifa filtrados
  const filteredTariffActs = useMemo(() => {
    if (!tariffSearch.trim()) return tariffActs;

    const query = tariffSearch.toLowerCase();
    return tariffActs.filter(
      (act) =>
        act.medicalActCode.toLowerCase().includes(query) ||
        act.medicalActName.toLowerCase().includes(query)
    );
  }, [tariffActs, tariffSearch]);

  // Computed: estadísticas MEJORADAS
  const stats = useMemo(() => {
    const totalActs = tariffActs.length;
    const availableActs = medicalActs.length - totalActs;
    const totalValue = tariffActs.reduce((sum, act) => sum + act.price, 0);
    
    let minPrice = 0;
    let maxPrice = 0;
    let avgPrice = 0;
    
    if (tariffActs.length > 0) {
      const prices = tariffActs.map(a => a.price);
      minPrice = Math.min(...prices);
      maxPrice = Math.max(...prices);
      avgPrice = totalValue / tariffActs.length;
    }
    
    return { 
      totalActs, 
      availableActs, 
      totalValue, 
      minPrice, 
      maxPrice,
      avgPrice 
    };
  }, [medicalActs.length, tariffActs]);

  // Format price
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-ES');
  };

  const formatDateForInput = (dateStr: string | null) => {
    if (!dateStr) return '';
    return dateStr.split('T')[0];
  };

  // Handlers básicos
  const handleCreate = () => {
    setEditingItem(null);
    setFormError(null);
    reset({
      policyId: filterPolicyId || '',
      code: '',
      name: '',
      description: '',
      validFrom: new Date().toISOString().split('T')[0],
      validTo: '',
    });
    setIsModalOpen(true);
  };

  const handleEdit = (item: Tariff) => {
    setEditingItem(item);
    setFormError(null);
    reset({
      policyId: item.policyId,
      code: item.code,
      name: item.name,
      description: item.description || '',
      validFrom: formatDateForInput(item.validFrom),
      validTo: formatDateForInput(item.validTo),
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!isSaving) {
      setIsModalOpen(false);
      setEditingItem(null);
      setFormError(null);
    }
  };

  const handleSave = async (formData: TariffFormData) => {
    // Limpiar error previo
    setFormError(null);

    // Validar código duplicado dentro de la misma póliza
    const codeUpper = formData.code.toUpperCase().trim();
    const existingItem = items.find(
      (item) =>
        item.code.toUpperCase() === codeUpper &&
        item.policyId === formData.policyId &&
        item.id !== editingItem?.id
    );

    if (existingItem) {
      const policyName = policies.find(p => p.id === formData.policyId)?.name || 'esta póliza';
      setFormError(`Ya existe una tarifa con el código "${codeUpper}" en ${policyName}`);
      return;
    }

    setIsSaving(true);
    try {
      if (editingItem) {
        await tariffsApi.update(editingItem.id, {
          policyId: formData.policyId,
          code: formData.code,
          name: formData.name,
          description: formData.description || undefined,
          validFrom: formData.validFrom,
          validTo: formData.validTo || null,
        });
        toast.success('Actualizada', `Tarifa "${formData.name}" actualizada`);
      } else {
        await tariffsApi.create({
          policyId: formData.policyId,
          code: formData.code,
          name: formData.name,
          description: formData.description || undefined,
          validFrom: formData.validFrom,
          validTo: formData.validTo || undefined,
        });
        toast.success('Creada', `Tarifa "${formData.name}" creada`);
      }
      setIsModalOpen(false);
      setEditingItem(null);
      setFormError(null);
      loadData();
    } catch (err: any) {
      const codeUpper = formData.code.toUpperCase().trim();
      setFormError(`Ya existe una tarifa con el código "${codeUpper}"`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = (item: Tariff) => {
    setConfirmDialog({
      isOpen: true,
      item,
      action: item.isActive ? 'deactivate' : 'activate',
    });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.item) return;
    setIsProcessing(true);
    try {
      if (confirmDialog.action === 'deactivate') {
        await tariffsApi.deactivate(confirmDialog.item.id);
        toast.success('Desactivada', `Tarifa "${confirmDialog.item.name}" desactivada`);
      } else {
        await tariffsApi.reactivate(confirmDialog.item.id);
        toast.success('Activada', `Tarifa "${confirmDialog.item.name}" activada`);
      }
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo cambiar el estado');
    } finally {
      setIsProcessing(false);
      setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' });
    }
  };

  // =========================================
  // MODAL ACTOS - HANDLERS REDISEÑADOS
  // =========================================

  const handleOpenActsModal = async (tariff: Tariff) => {
    setSelectedTariff(tariff);
    setIsActsModalOpen(true);
    setIsLoadingActs(true);
    setAvailableSearch('');
    setTariffSearch('');
    setSelectedAvailableIds(new Set());
    setBatchPrice('');
    setEditingPriceId(null);
    try {
      const acts = await tariffsApi.getTariffActs(tariff.id);
      setTariffActs(acts);
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudieron cargar los actos');
    } finally {
      setIsLoadingActs(false);
    }
  };

  const handleCloseActsModal = () => {
    setIsActsModalOpen(false);
    setSelectedTariff(null);
    setTariffActs([]);
    loadData();
  };

  // Toggle selección individual
  const handleToggleAvailableSelect = (actId: string) => {
    setSelectedAvailableIds((prev) => {
      const next = new Set(prev);
      if (next.has(actId)) {
        next.delete(actId);
      } else {
        next.add(actId);
      }
      return next;
    });
  };

  // Seleccionar todos los visibles
  const handleSelectAllVisible = () => {
    const visibleIds = filteredAvailableActs.map((a) => a.id);
    setSelectedAvailableIds(new Set(visibleIds));
  };

  // Deseleccionar todos
  const handleDeselectAll = () => {
    setSelectedAvailableIds(new Set());
  };

  // Añadir actos seleccionados con precio batch - CORREGIDO: permite precio 0
  const handleAddSelectedActs = async () => {
    if (!selectedTariff || selectedAvailableIds.size === 0) return;

    const price = parseFloat(batchPrice);
    if (isNaN(price) || price < 0) {
      toast.error('Error', 'Introduce un precio válido (0 o mayor)');
      return;
    }

    setIsSavingActs(true);
    try {
      const acts = Array.from(selectedAvailableIds).map((medicalActId) => ({
        medicalActId,
        price,
      }));

      await tariffsApi.batchUpdateTariffActs(selectedTariff.id, { acts });

      const updatedActs = await tariffsApi.getTariffActs(selectedTariff.id);
      setTariffActs(updatedActs);
      setSelectedAvailableIds(new Set());
      setBatchPrice('');

      toast.success('Añadidos', `${acts.length} actos añadidos a la tarifa`);
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudieron añadir los actos');
    } finally {
      setIsSavingActs(false);
    }
  };

  // Añadir un acto individual con su precio por defecto
  const handleAddSingleAct = async (act: MedicalActSelect) => {
    if (!selectedTariff) return;

    const price = act.defaultPrice >= 0 ? act.defaultPrice : 0;

    setIsSavingActs(true);
    try {
      await tariffsApi.addTariffAct(selectedTariff.id, {
        medicalActId: act.id,
        price,
      });

      const updatedActs = await tariffsApi.getTariffActs(selectedTariff.id);
      setTariffActs(updatedActs);

      toast.success('Añadido', `"${act.name}" añadido`);
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo añadir');
    } finally {
      setIsSavingActs(false);
    }
  };

  // Editar precio inline
  const handleStartEditPrice = (act: TariffAct) => {
    setEditingPriceId(act.medicalActId);
    setEditingPriceValue(act.price.toString());
  };

  // Guardar precio - CORREGIDO: permite precio 0
  const handleSavePrice = async () => {
    if (!selectedTariff || !editingPriceId) return;

    const price = parseFloat(editingPriceValue);
    if (isNaN(price) || price < 0) {
      toast.error('Error', 'El precio debe ser 0 o mayor');
      return;
    }

    setIsSavingActs(true);
    try {
      await tariffsApi.updateTariffAct(selectedTariff.id, editingPriceId, { price });

      setTariffActs((prev) =>
        prev.map((a) => (a.medicalActId === editingPriceId ? { ...a, price } : a))
      );
      setEditingPriceId(null);
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo actualizar');
    } finally {
      setIsSavingActs(false);
    }
  };

  const handleCancelEditPrice = () => {
    setEditingPriceId(null);
    setEditingPriceValue('');
  };

  // Eliminar acto de tarifa
  const handleRemoveAct = async (actId: string) => {
    if (!selectedTariff) return;

    setIsSavingActs(true);
    try {
      await tariffsApi.removeTariffAct(selectedTariff.id, actId);
      setTariffActs((prev) => prev.filter((a) => a.medicalActId !== actId));
      toast.success('Eliminado', 'Acto eliminado de la tarifa');
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo eliminar');
    } finally {
      setIsSavingActs(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tarifas</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestión de tarifas y precios de actos
          </p>
        </div>
      </div>

      {/* Toolbar - Nuevo diseño */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4">
        {/* Fila superior: Búsqueda y acciones principales */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Búsqueda y botón filtros */}
          <div className="flex items-center flex-1 max-w-2xl gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por código, nombre o póliza..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showFilters || filterPolicyId
                  ? 'bg-blue-50 border-blue-200 text-blue-600' 
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filtros
            </button>
          </div>

          {/* Acciones */}
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualizar"
            >
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setIsBulkImportOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
              title="Cargar tarifa completa con todos sus actos desde Excel"
            >
              <Package className="h-4 w-4" />
              Carga Masiva
            </button>
            <button
              onClick={() => setIsImportExportOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-blue-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Importar / Exportar
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 shadow-md shadow-blue-100 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nueva tarifa
            </button>
          </div>
        </div>

        {/* Fila de filtros activos (chips) */}
        {(showInactive || filterPolicyId || searchQuery) && (
          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-2">
              Filtros activos:
            </span>
            
            {showInactive && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Estado: Incluye inactivos
                <button 
                  onClick={() => setShowInactive(false)}
                  className="ml-1.5 hover:text-blue-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            {filterPolicyId && (
              <div className="inline-flex items-center bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-xs font-medium">
                Póliza: {policies.find(p => p.id === filterPolicyId)?.name || 'Seleccionada'}
                <button 
                  onClick={() => setFilterPolicyId('')}
                  className="ml-1.5 hover:text-purple-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            {searchQuery && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Búsqueda: "{searchQuery}"
                <button 
                  onClick={() => setSearchQuery('')}
                  className="ml-1.5 hover:text-blue-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            <button 
              onClick={() => {
                setShowInactive(false);
                setFilterPolicyId('');
                setSearchQuery('');
              }}
              className="text-xs text-gray-400 hover:text-red-500 font-medium ml-2 underline underline-offset-2"
            >
              Limpiar todo
            </button>
          </div>
        )}

        {/* Panel de filtros */}
        {showFilters && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-t border-gray-100 pt-3 gap-4">
            {/* Checkbox, póliza y ordenación */}
            <div className="flex items-center gap-6 flex-wrap">
              <label className="inline-flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                  Mostrar inactivas
                </span>
              </label>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Póliza:</span>
                <select
                  value={filterPolicyId}
                  onChange={(e) => setFilterPolicyId(e.target.value)}
                  className="text-sm border-none bg-transparent py-0 pl-0 pr-8 font-medium text-gray-700 focus:ring-0 cursor-pointer hover:text-blue-600 transition-colors"
                >
                  <option value="">Todas</option>
                  {policies.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.code} - {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ordenar por:</span>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field as SortField);
                    setSortOrder(order as 'asc' | 'desc');
                  }}
                  className="text-sm border-none bg-transparent py-0 pl-0 pr-8 font-medium text-gray-700 focus:ring-0 cursor-pointer hover:text-blue-600 transition-colors"
                >
                  <option value="name-asc">Nombre (A-Z)</option>
                  <option value="name-desc">Nombre (Z-A)</option>
                  <option value="code-asc">Código (A-Z)</option>
                  <option value="code-desc">Código (Z-A)</option>
                  <option value="createdAt-desc">Fecha (Más reciente)</option>
                  <option value="createdAt-asc">Fecha (Antiguo)</option>
                </select>
              </div>
            </div>

            {/* Items por página */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">Ver:</span>
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setLimit(10)}
                  className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 transition-colors ${
                    limit === 10 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  10
                </button>
                <button
                  onClick={() => setLimit(25)}
                  className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 transition-colors ${
                    limit === 25 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  25
                </button>
                <button
                  onClick={() => setLimit(50)}
                  className={`px-3 py-1 text-xs font-semibold transition-colors ${
                    limit === 50 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  50
                </button>
              </div>
              <span className="text-sm text-gray-500">por página</span>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {paginatedItems.length === 0 && !isLoading ? (
          <div className="p-12 text-center">
            <Receipt className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {searchQuery || filterPolicyId ? 'Sin resultados' : 'No hay tarifas'}
            </h3>
            <p className="text-gray-500">
              {searchQuery || filterPolicyId
                ? 'No se encontraron tarifas con ese criterio'
                : 'Crea tu primera tarifa'}
            </p>
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Póliza
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                    Vigencia
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                    Actos
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedItems.map((item) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-50 ${!item.isActive ? 'bg-gray-50/50' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-lg">
                        {item.code}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`font-medium ${
                          item.isActive ? 'text-gray-900' : 'text-gray-500'
                        }`}
                      >
                        {item.name}
                      </span>
                      {item.description && (
                        <p className="text-xs text-gray-500 truncate max-w-xs">
                          {item.description}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-700">{item.policyName}</span>
                        </div>
                        {item.insuranceCompanyName && (
                          <p className="text-xs text-gray-500 ml-6">
                            {item.insuranceCompanyName}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-xs text-gray-600">
                        <div className="flex items-center justify-center gap-1">
                          <Calendar className="h-3.5 w-3.5 text-gray-400" />
                          {formatDate(item.validFrom)}
                        </div>
                        {item.validTo && (
                          <div className="text-gray-400">
                            hasta {formatDate(item.validTo)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleOpenActsModal(item)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <Stethoscope className="h-4 w-4" />
                        {item.actsCount ?? 0} actos
                        <ChevronRight className="h-3 w-3" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleToggleStatus(item)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                          item.isActive ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                        role="switch"
                        aria-checked={item.isActive}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                            item.isActive ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        >
                          <Power
                            className={`h-3 w-3 absolute top-1 left-1 ${
                              item.isActive ? 'text-blue-600' : 'text-gray-400'
                            }`}
                          />
                        </span>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                        title="Editar"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Mostrando {((page - 1) * limit) + 1} - {Math.min(page * limit, totalItems)} de {totalItems}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm text-gray-600">
                  Página {page} de {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingItem ? 'Editar tarifa' : 'Nueva tarifa'}
        subtitle={editingItem ? `Editando: ${editingItem.name}` : 'Completa los datos'}
        size="md"
      >
        <form onSubmit={handleSubmit(handleSave)}>
          {/* Error banner dentro del modal */}
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="h-4 w-4 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-red-800">Error de validación</p>
                <p className="text-sm text-red-600 mt-0.5">{formError}</p>
              </div>
              <button
                type="button"
                onClick={() => setFormError(null)}
                className="flex-shrink-0 p-1 text-red-400 hover:text-red-600 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Póliza *
              </label>
              <select
                {...register('policyId', { required: 'Selecciona una póliza' })}
                onChange={() => formError && setFormError(null)}
                className={`w-full px-4 py-2.5 border rounded-xl text-sm ${
                  errors.policyId ? 'border-red-300' : 'border-gray-200'
                }`}
              >
                <option value="">Seleccionar...</option>
                {policies.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} - {p.name}
                    {p.insuranceCompanyName ? ` (${p.insuranceCompanyName})` : ''}
                  </option>
                ))}
              </select>
              {errors.policyId && (
                <p className="mt-1 text-xs text-red-500">
                  <AlertCircle className="h-3 w-3 inline mr-1" />
                  {errors.policyId.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código *
                </label>
                <input
                  type="text"
                  {...register('code', { 
                    required: 'El código es obligatorio',
                    maxLength: { value: 20, message: 'Máximo 20 caracteres' }
                  })}
                  onChange={() => formError && setFormError(null)}
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm uppercase ${
                    errors.code || formError ? 'border-red-300 bg-red-50/50' : 'border-gray-200'
                  }`}
                  placeholder="TAR001"
                />
                {errors.code && (
                  <p className="mt-1 text-xs text-red-500">
                    <AlertCircle className="h-3 w-3 inline mr-1" />
                    {errors.code.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  {...register('name', { 
                    required: 'El nombre es obligatorio',
                    maxLength: { value: 100, message: 'Máximo 100 caracteres' }
                  })}
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm ${
                    errors.name ? 'border-red-300' : 'border-gray-200'
                  }`}
                  placeholder="Nombre de la tarifa"
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-500">
                    <AlertCircle className="h-3 w-3 inline mr-1" />
                    {errors.name.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción
              </label>
              <textarea
                {...register('description')}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none"
                placeholder="Descripción opcional..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Válida desde *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="date"
                    {...register('validFrom', { required: 'La fecha de inicio es obligatoria' })}
                    className={`w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm ${
                      errors.validFrom ? 'border-red-300' : 'border-gray-200'
                    }`}
                  />
                </div>
                {errors.validFrom && (
                  <p className="mt-1 text-xs text-red-500">
                    <AlertCircle className="h-3 w-3 inline mr-1" />
                    {errors.validFrom.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Válida hasta
                  <span className="text-xs text-gray-400 ml-1">(opcional)</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="date"
                    {...register('validTo')}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          <ModalFooter>
            <button
              type="button"
              onClick={handleCloseModal}
              disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {editingItem ? 'Guardar' : 'Crear'}
                </>
              )}
            </button>
          </ModalFooter>
        </form>
      </Modal>

      {/* =========================================
          MODAL ACTOS REDISEÑADO - VISTA DUAL
          CORREGIDO: Stats bar con info útil
          ========================================= */}
      <Modal
        isOpen={isActsModalOpen}
        onClose={handleCloseActsModal}
        title="Gestión de actos en tarifa"
        subtitle={selectedTariff ? `${selectedTariff.code} - ${selectedTariff.name}` : ''}
        size="xl"
      >
        {isLoadingActs ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Stats Bar - MEJORADO: Info útil en vez de % cobertura */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Actos en tarifa */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Hash className="h-4 w-4 text-blue-600" />
                  <span className="text-2xl font-bold text-blue-700">{stats.totalActs}</span>
                </div>
                <p className="text-xs text-blue-600 font-medium">Actos en tarifa</p>
              </div>

              {/* Disponibles */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Stethoscope className="h-4 w-4 text-gray-500" />
                  <span className="text-2xl font-bold text-gray-600">{stats.availableActs}</span>
                </div>
                <p className="text-xs text-gray-500 font-medium">Sin añadir</p>
              </div>

              {/* Valor total */}
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-3 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Euro className="h-4 w-4 text-emerald-600" />
                  <span className="text-lg font-bold text-emerald-700">{formatPrice(stats.totalValue)}</span>
                </div>
                <p className="text-xs text-emerald-600 font-medium">Valor total</p>
              </div>

              {/* Rango de precios */}
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-3 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <TrendingUp className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-bold text-amber-700">
                    {stats.totalActs > 0 
                      ? `${formatPrice(stats.minPrice)} - ${formatPrice(stats.maxPrice)}`
                      : '-'
                    }
                  </span>
                </div>
                <p className="text-xs text-amber-600 font-medium">Rango precios</p>
              </div>
            </div>

            {/* Vista dual */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Panel izquierdo: Actos disponibles */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                      <Stethoscope className="h-4 w-4 text-gray-400" />
                      Actos disponibles
                      <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">
                        {filteredAvailableActs.length}
                      </span>
                    </h4>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={availableSearch}
                      onChange={(e) => setAvailableSearch(e.target.value)}
                      placeholder="Buscar actos..."
                      className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg"
                    />
                  </div>
                </div>

                {/* Batch add controls */}
                {selectedAvailableIds.size > 0 && (
                  <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
                    <span className="text-sm font-medium text-blue-700">
                      {selectedAvailableIds.size} seleccionados
                    </span>
                    <button
                      onClick={handleDeselectAll}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Deseleccionar
                    </button>
                    <div className="flex-1" />
                    <div className="relative w-24">
                      <Euro className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={batchPrice}
                        onChange={(e) => setBatchPrice(e.target.value)}
                        placeholder="Precio"
                        className="w-full pl-6 pr-2 py-1 text-sm border border-blue-200 rounded"
                      />
                    </div>
                    <button
                      onClick={handleAddSelectedActs}
                      disabled={isSavingActs || batchPrice === ''}
                      className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                    >
                      <Plus className="h-3 w-3" />
                      Añadir
                    </button>
                  </div>
                )}

                {/* Select all button */}
                {filteredAvailableActs.length > 0 && selectedAvailableIds.size === 0 && (
                  <div className="px-4 py-2 border-b border-gray-100">
                    <button
                      onClick={handleSelectAllVisible}
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <Check className="h-3 w-3" />
                      Seleccionar todos ({filteredAvailableActs.length})
                    </button>
                  </div>
                )}

                {/* Lista */}
                <div className="max-h-[350px] overflow-y-auto">
                  {filteredAvailableActs.length === 0 ? (
                    <div className="py-12 text-center text-gray-500">
                      <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-400" />
                      <p className="text-sm">Todos los actos están en la tarifa</p>
                    </div>
                  ) : (
                    filteredAvailableActs.map((act) => (
                      <div
                        key={act.id}
                        className={`flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                          selectedAvailableIds.has(act.id) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedAvailableIds.has(act.id)}
                          onChange={() => handleToggleAvailableSelect(act.id)}
                          className="rounded border-gray-300 text-blue-600"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-bold rounded">
                              {act.code}
                            </span>
                            <span className="text-sm text-gray-900 truncate">
                              {act.name}
                            </span>
                          </div>
                          {act.defaultPrice > 0 && (
                            <span className="text-xs text-gray-400">
                              Base: {formatPrice(act.defaultPrice)}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleAddSingleAct(act)}
                          disabled={isSavingActs}
                          className="p-1.5 text-emerald-600 hover:bg-emerald-100 rounded-lg transition-colors"
                          title="Añadir con precio base"
                        >
                          <ArrowRight className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Panel derecho: Actos en tarifa */}
              <div className="border border-emerald-200 rounded-xl overflow-hidden">
                <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-emerald-700 flex items-center gap-2">
                      <Receipt className="h-4 w-4" />
                      En esta tarifa
                      <span className="text-xs bg-emerald-200 text-emerald-700 px-1.5 py-0.5 rounded-full">
                        {tariffActs.length}
                      </span>
                    </h4>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={tariffSearch}
                      onChange={(e) => setTariffSearch(e.target.value)}
                      placeholder="Buscar en tarifa..."
                      className="w-full pl-8 pr-3 py-1.5 text-sm border border-emerald-200 rounded-lg"
                    />
                  </div>
                </div>

                {/* Lista */}
                <div className="max-h-[400px] overflow-y-auto">
                  {filteredTariffActs.length === 0 ? (
                    <div className="py-12 text-center text-gray-500">
                      <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-amber-400" />
                      <p className="text-sm">
                        {tariffSearch ? 'Sin resultados' : 'Sin actos en esta tarifa'}
                      </p>
                    </div>
                  ) : (
                    filteredTariffActs.map((act) => (
                      <div
                        key={act.medicalActId}
                        className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-bold rounded">
                              {act.medicalActCode}
                            </span>
                            <span className="text-sm text-gray-900 truncate">
                              {act.medicalActName}
                            </span>
                          </div>
                          {act.treatmentGroupName && (
                            <span className="text-xs text-gray-400">
                              {act.treatmentGroupName}
                            </span>
                          )}
                        </div>

                        {editingPriceId === act.medicalActId ? (
                          <div className="flex items-center gap-1">
                            <div className="relative w-20">
                              <Euro className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editingPriceValue}
                                onChange={(e) => setEditingPriceValue(e.target.value)}
                                className="w-full pl-6 pr-1 py-1 text-sm border border-emerald-300 rounded"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSavePrice();
                                  if (e.key === 'Escape') handleCancelEditPrice();
                                }}
                              />
                            </div>
                            <button
                              onClick={handleSavePrice}
                              disabled={isSavingActs}
                              className="p-1 text-emerald-600 hover:bg-emerald-100 rounded"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={handleCancelEditPrice}
                              className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => handleStartEditPrice(act)}
                              className={`px-2 py-1 text-sm font-medium rounded hover:opacity-80 transition-colors ${
                                act.price === 0 
                                  ? 'bg-amber-100 text-amber-700' 
                                  : 'bg-emerald-100 text-emerald-700'
                              }`}
                            >
                              {formatPrice(act.price)}
                            </button>
                            <button
                              onClick={() => handleRemoveAct(act.medicalActId)}
                              disabled={isSavingActs}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Eliminar de tarifa"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Footer info */}
            <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-500">
              <strong>💡 Tips:</strong> Selecciona múltiples actos y asigna un precio común (puede ser 0€). 
              Haz clic en el precio para editarlo. Los actos con precio 0€ se muestran en ámbar.
            </div>
          </div>
        )}

        <ModalFooter>
          <button
            onClick={handleCloseActsModal}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            Cerrar
          </button>
        </ModalFooter>
      </Modal>

      {/* Import/Export Modal */}
      <ImportExportModal
        isOpen={isImportExportOpen}
        onClose={() => setIsImportExportOpen(false)}
        title="Tarifas"
        onExport={tariffsImportExport.export}
        onDownloadTemplate={tariffsImportExport.downloadTemplate}
        onPreview={tariffsImportExport.preview}
        onImport={tariffsImportExport.import}
        onImportSuccess={loadData}
        previewColumns={[
          { key: 'policyCode', label: 'Póliza' },
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
          { key: 'validFrom', label: 'Desde' },
        ]}
      />

      {/* Bulk Import Modal - Carga Masiva de Tarifa Completa */}
      <Modal
        isOpen={isBulkImportOpen}
        onClose={() => {
          if (!isBulkImportLoading) {
            setIsBulkImportOpen(false);
            setBulkImportFile(null);
            setBulkImportPreview(null);
            setBulkImportResult(null);
          }
        }}
        title="📦 Carga Masiva de Tarifa Completa"
        subtitle="Importa una tarifa con todos sus actos y precios en un solo paso"
        size="lg"
      >
        <div className="space-y-5">
          {/* Info banner explicativo */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div className="text-sm">
                <p className="font-semibold text-blue-900 mb-2">
                  ¿Qué es la carga masiva?
                </p>
                <p className="text-blue-700 mb-2">
                  Permite crear o actualizar una <strong>tarifa completa</strong> incluyendo todos sus actos 
                  médicos y precios desde un único archivo Excel.
                </p>
                <p className="text-blue-600 text-xs">
                  💡 <strong>Ideal para:</strong> Carga inicial de tarifas, actualización anual de precios, 
                  o migrar tarifas desde otro sistema.
                </p>
              </div>
            </div>
          </div>

          {/* Step 1: Download template */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-sm">
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Descarga la plantilla Excel</h4>
                  <p className="text-xs text-gray-500">Incluye todas las columnas necesarias con ejemplos</p>
                </div>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800 font-medium mb-2">📋 Columnas de la plantilla:</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-amber-700">
                  <div>• <strong>Código Aseguradora</strong> - Ej: ASISA</div>
                  <div>• <strong>Código Póliza</strong> - Ej: DENTAL-PLUS</div>
                  <div>• <strong>Código Tarifa</strong> - Ej: TAR2025</div>
                  <div>• <strong>Nombre Tarifa</strong> - Ej: Tarifa 2025</div>
                  <div>• <strong>Válida Desde</strong> - Ej: 2025-01-01</div>
                  <div>• <strong>Válida Hasta</strong> - (opcional)</div>
                  <div>• <strong>Código Acto</strong> - Ej: LIMP001</div>
                  <div>• <strong>Precio</strong> - Ej: 45.00</div>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800 font-medium mb-1">💡 Importante:</p>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• Cada fila representa <strong>un acto</strong> de la tarifa</li>
                  <li>• Repite los datos de la tarifa en cada fila (Aseguradora, Póliza, Código Tarifa...)</li>
                  <li>• Los códigos de Aseguradora, Póliza y Acto <strong>deben existir</strong> en el sistema</li>
                  <li>• Si la tarifa ya existe, se actualizarán los precios</li>
                </ul>
              </div>

              <button
                onClick={async () => {
                  try {
                    await tariffsBulkImport.downloadTemplate();
                    toast.success('Descargada', 'Plantilla descargada correctamente');
                  } catch (err: any) {
                    toast.error('Error', err.message || 'No se pudo descargar la plantilla');
                  }
                }}
                className="w-full inline-flex items-center justify-center px-4 py-3 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <Download className="h-5 w-5 mr-2" />
                Descargar plantilla Excel
              </button>
            </div>
          </div>

          {/* Step 2: Upload file */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  bulkImportFile ? 'bg-emerald-500 text-white' : 'bg-blue-500 text-white'
                }`}>
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Sube tu archivo completado</h4>
                  <p className="text-xs text-gray-500">Se analizará automáticamente antes de importar</p>
                </div>
              </div>
            </div>
            <div className="p-4">
              <input
                ref={bulkImportInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setBulkImportFile(file);
                  setBulkImportPreview(null);
                  setBulkImportResult(null);
                  
                  // Auto preview
                  setIsBulkImportLoading(true);
                  try {
                    const preview = await tariffsBulkImport.preview(file);
                    setBulkImportPreview(preview);
                  } catch (err: any) {
                    toast.error('Error', err.message || 'No se pudo analizar el archivo');
                  } finally {
                    setIsBulkImportLoading(false);
                  }
                }}
              />
              
              <div 
                onClick={() => !isBulkImportLoading && bulkImportInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                  bulkImportFile 
                    ? 'border-emerald-300 bg-emerald-50' 
                    : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                {bulkImportFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileSpreadsheet className="h-8 w-8 text-emerald-600" />
                    <div className="text-left">
                      <p className="font-medium text-emerald-700">{bulkImportFile.name}</p>
                      <p className="text-xs text-emerald-600">
                        {(bulkImportFile.size / 1024).toFixed(1)} KB • Haz clic para cambiar
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 font-medium">
                      Haz clic para seleccionar archivo
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Formatos: .xlsx, .xls
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Loading */}
          {isBulkImportLoading && (
            <div className="flex items-center justify-center py-6 bg-gray-50 rounded-xl">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600 font-medium">Analizando archivo...</span>
            </div>
          )}

          {/* Step 3: Preview */}
          {bulkImportPreview && !isBulkImportLoading && !bulkImportResult && (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    bulkImportPreview.valid ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
                  }`}>
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Vista previa</h4>
                    <p className="text-xs text-gray-500">
                      {bulkImportPreview.valid 
                        ? 'Todo correcto, listo para importar' 
                        : 'Hay errores que corregir'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-4">
                {bulkImportPreview.valid ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 p-3 rounded-lg">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="font-medium">Archivo válido - Sin errores</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-blue-600">
                          {bulkImportPreview.tariffs?.length || 0}
                        </p>
                        <p className="text-sm text-blue-700">Tarifas a procesar</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-purple-600">
                          {bulkImportPreview.totalRows || 0}
                        </p>
                        <p className="text-sm text-purple-700">Actos totales</p>
                      </div>
                    </div>

                    {/* Detalle por tarifa */}
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {bulkImportPreview.tariffs?.map((tariff, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">
                              {tariff.tariffCode} - {tariff.tariffName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {tariff.insuranceCompanyCode} → {tariff.policyCode} | 
                              Desde: {tariff.validFrom}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="inline-flex items-center px-2 py-1 bg-emerald-100 text-emerald-700 text-sm font-medium rounded">
                              {tariff.validActsCount} actos
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={async () => {
                        if (!bulkImportFile) return;
                        setIsBulkImportLoading(true);
                        try {
                          const result = await tariffsBulkImport.import(bulkImportFile);
                          setBulkImportResult(result);
                          if (result.success) {
                            toast.success('¡Importación completada!', 
                              `${result.tariffsCreated + result.tariffsUpdated} tarifas, ${result.actsCreated + result.actsUpdated} actos`);
                            loadData();
                          }
                        } catch (err: any) {
                          toast.error('Error', err.message || 'No se pudo importar');
                        } finally {
                          setIsBulkImportLoading(false);
                        }
                      }}
                      disabled={isBulkImportLoading}
                      className="w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <Upload className="h-5 w-5" />
                      Importar tarifa{bulkImportPreview.tariffs?.length > 1 ? 's' : ''}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
                      <AlertTriangle className="h-5 w-5" />
                      <span className="font-medium">El archivo tiene errores</span>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm font-medium text-red-800 mb-2">Errores encontrados:</p>
                      <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                        {bulkImportPreview.errors?.map((err, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-red-400">•</span>
                            {err}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <p className="text-sm text-gray-600">
                      Corrige los errores en el archivo Excel y vuelve a subirlo.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Result */}
          {bulkImportResult && (
            <div className={`border rounded-xl overflow-hidden ${
              bulkImportResult.success 
                ? 'border-emerald-200' 
                : 'border-red-200'
            }`}>
              <div className={`px-4 py-3 ${
                bulkImportResult.success ? 'bg-emerald-50' : 'bg-red-50'
              }`}>
                {bulkImportResult.success ? (
                  <div className="flex items-center gap-2 text-emerald-700">
                    <CheckCircle2 className="h-6 w-6" />
                    <span className="font-semibold text-lg">¡Importación completada!</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="h-6 w-6" />
                    <span className="font-semibold text-lg">Errores en la importación</span>
                  </div>
                )}
              </div>
              <div className="p-4">
                {bulkImportResult.success ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-emerald-100 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-700">{bulkImportResult.tariffsCreated}</p>
                      <p className="text-xs text-emerald-600">Tarifas creadas</p>
                    </div>
                    <div className="bg-blue-100 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-700">{bulkImportResult.tariffsUpdated}</p>
                      <p className="text-xs text-blue-600">Tarifas actualizadas</p>
                    </div>
                    <div className="bg-purple-100 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-purple-700">{bulkImportResult.actsCreated}</p>
                      <p className="text-xs text-purple-600">Actos añadidos</p>
                    </div>
                    <div className="bg-amber-100 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-amber-700">{bulkImportResult.actsUpdated}</p>
                      <p className="text-xs text-amber-600">Actos actualizados</p>
                    </div>
                  </div>
                ) : (
                  <ul className="text-sm text-red-600 space-y-1">
                    {bulkImportResult.errors?.map((err, i) => (
                      <li key={i}>• {err}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        <ModalFooter>
          <button
            onClick={() => {
              setIsBulkImportOpen(false);
              setBulkImportFile(null);
              setBulkImportPreview(null);
              setBulkImportResult(null);
            }}
            disabled={isBulkImportLoading}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50"
          >
            {bulkImportResult?.success ? 'Cerrar' : 'Cancelar'}
          </button>
        </ModalFooter>
      </Modal>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() =>
          !isProcessing &&
          setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' })
        }
        onConfirm={handleConfirmToggle}
        isLoading={isProcessing}
        variant={confirmDialog.action === 'deactivate' ? 'warning' : 'info'}
        title={
          confirmDialog.action === 'deactivate'
            ? '¿Desactivar tarifa?'
            : '¿Activar tarifa?'
        }
        message={
          confirmDialog.action === 'deactivate'
            ? `La tarifa "${confirmDialog.item?.name}" será desactivada.`
            : `La tarifa "${confirmDialog.item?.name}" será reactivada.`
        }
        confirmText={confirmDialog.action === 'deactivate' ? 'Desactivar' : 'Activar'}
      />
    </div>
  );
}

export default TariffsAdminPage;
